import React, { useEffect, useState } from 'react';
import { Box, Button, InputAdornment, MenuItem, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ScrollableDialog from '../common/ScrollableDialog';
import PrimaryActionButton from '../common/PrimaryActionButton';
import SalovareSelectField from '../common/SalovareSelectField';
import PackageQuantityInput from './PackageQuantityInput';
import { buildAdornmentTextFieldSx, fieldLabelSx, nativeInputSx } from '../common/formFieldStyles';
import { STOCK_ADJUSTMENT_REASONS } from '../../constants/Stock';
import { IN_STORE_PAYMENT_METHODS, getStorePaymentMethodKey } from '../../constants/StorePaymentMethod';
import { getCurrencySymbol } from '../../utils/currencyUtils';
import { getLocalizedValue } from '../../constants/Languages';
import { buildStockAdjustmentPayload, validateStockAdjustment } from '../../utils/stockAdjustmentValidation';
import { toPackageQuantity } from '../../utils/stockQuantityUtils';

const StockAdjustmentDialog = ({ open, products = [], currency = 'EUR', saving = false, onClose, onSave }) => {
  const { t, i18n } = useTranslation();
  const [form, setForm] = useState({
    productId: '',
    reason: 'Damaged',
    packageQuantity: '',
    countedPackageQuantity: '',
    amountReturned: '',
    refundPaymentMethod: 'Cash',
    notes: '',
  });
  const [errors, setErrors] = useState({});
  const product = products.find((item) => String(item.id) === String(form.productId));
  const onHandPackages = product
    ? toPackageQuantity(product.stockOnHand, product.contentQuantity)
    : null;

  useEffect(() => {
    if (open) {
      setForm({
        productId: '',
        reason: 'Damaged',
        packageQuantity: '',
        countedPackageQuantity: '',
        amountReturned: '',
        refundPaymentMethod: 'Cash',
        notes: '',
      });
      setErrors({});
    }
  }, [open]);

  const handleSubmit = () => {
    const nextErrors = validateStockAdjustment({ ...form, onHandPackages });
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    onSave(buildStockAdjustmentPayload(form));
  };

  return (
    <ScrollableDialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="md"
      title={t('stock.adjustments.title')}
      dividers
      showCloseButton
      titleSx={{ pb: 1, flexShrink: 0 }}
      actionsSx={{ flexShrink: 0, justifyContent: 'space-between' }}
      actions={(
        <>
          <Button onClick={onClose} disabled={saving} color="inherit">{t('common.cancel')}</Button>
          <PrimaryActionButton id="stock-adjustment-save" onClick={handleSubmit} disabled={saving}>
            {saving ? t('common.saving') : t('stock.adjustments.save')}
          </PrimaryActionButton>
        </>
      )}
    >
      <Box id="stock-adjustment-form" sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <Typography variant="body2" color="text.secondary">
          {t('stock.adjustments.decreaseOnly')}
        </Typography>
        <SalovareSelectField
          id="adjustment-product"
          label={t('stock.adjustments.product')}
          required
          value={form.productId}
          error={Boolean(errors.productId)}
          helperText={errors.productId ? t(errors.productId) : undefined}
          onChange={(event) => setForm((previous) => ({ ...previous, productId: event.target.value }))}
        >
          {products.map((item) => (
            <MenuItem key={item.id} value={item.id}>
              {typeof item.name === 'string' ? item.name : getLocalizedValue(item.name, i18n.language)}
            </MenuItem>
          ))}
        </SalovareSelectField>
        <SalovareSelectField
          id="adjustment-reason"
          label={t('stock.adjustments.reason')}
          required
          value={form.reason}
          onChange={(event) => setForm((previous) => ({ ...previous, reason: event.target.value }))}
        >
          {STOCK_ADJUSTMENT_REASONS.map((reason) => (
            <MenuItem key={reason} value={reason}>{t(`stock.reasons.${reason}`)}</MenuItem>
          ))}
        </SalovareSelectField>
        {form.reason === 'CountCorrection' ? (
          <PackageQuantityInput
            id="adjustment-counted"
            label={t('stock.adjustments.counted')}
            required
            value={form.countedPackageQuantity}
            helperText={t('stock.adjustments.countedHelp', { onHand: onHandPackages ?? '—' })}
            error={errors.countedPackageQuantity ? t(errors.countedPackageQuantity) : undefined}
            onChange={(countedPackageQuantity) => setForm((previous) => ({ ...previous, countedPackageQuantity }))}
          />
        ) : (
          <PackageQuantityInput
            id="adjustment-removed"
            label={t('stock.adjustments.removed')}
            required
            value={form.packageQuantity}
            helperText={t('stock.adjustments.removedHelp')}
            error={errors.packageQuantity ? t(errors.packageQuantity) : undefined}
            onChange={(packageQuantity) => setForm((previous) => ({ ...previous, packageQuantity }))}
          />
        )}
        {form.reason === 'ReturnedToSupplier' ? (
          <>
            <Box>
              <Typography variant="subtitle2" sx={fieldLabelSx}>{t('stock.adjustments.amountReturned')} *</Typography>
              <TextField
                id="adjustment-amount-returned"
                type="number"
                value={form.amountReturned}
                onChange={(event) => setForm((previous) => ({ ...previous, amountReturned: event.target.value }))}
                inputProps={{
                  min: 0,
                  step: 0.01,
                  'aria-label': t('stock.adjustments.amountReturned'),
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">{getCurrencySymbol(currency)}</InputAdornment>
                  ),
                }}
                error={Boolean(errors.amountReturned)}
                helperText={errors.amountReturned ? t(errors.amountReturned) : t('stock.adjustments.amountReturnedHelp')}
                sx={buildAdornmentTextFieldSx(Boolean(errors.amountReturned))}
                fullWidth
              />
            </Box>
            {Number(form.amountReturned) > 0 ? (
              <SalovareSelectField
                id="adjustment-refund-method"
                label={t('stock.adjustments.refundPaymentMethod')}
                required
                value={form.refundPaymentMethod}
                error={Boolean(errors.refundPaymentMethod)}
                helperText={errors.refundPaymentMethod ? t(errors.refundPaymentMethod) : undefined}
                onChange={(event) => setForm((previous) => ({ ...previous, refundPaymentMethod: event.target.value }))}
              >
                {IN_STORE_PAYMENT_METHODS.map((method) => (
                  <MenuItem key={method} value={method}>{t(`storePaymentMethods.${getStorePaymentMethodKey(method)}`)}</MenuItem>
                ))}
              </SalovareSelectField>
            ) : null}
          </>
        ) : null}
        <Box>
          <Typography variant="subtitle2" sx={fieldLabelSx}>{t('stock.adjustments.notes')}</Typography>
          <Box
            component="textarea"
            id="adjustment-notes"
            aria-label={t('stock.adjustments.notes')}
            value={form.notes}
            onChange={(event) => setForm((previous) => ({ ...previous, notes: event.target.value }))}
            sx={nativeInputSx(false, true)}
          />
        </Box>
      </Box>
    </ScrollableDialog>
  );
};

export default StockAdjustmentDialog;
